"use client"

import React, { useState, useEffect } from "react"
import { X, Paperclip } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useClientRequestsApi, getAttachmentDownloadUrl } from "@/hooks/use-client-requests-api"
import {
  REQUEST_TYPE_TRANSLATION_KEY,
  STATUS_TRANSLATION_KEY,
  STATUS_BADGE_STYLES,
} from "@/lib/status-utils"
import { StatusBadge } from "@/components/grid/status-badge"
import { formatDateOnly } from "@/utils/format-helpers"
import { useAuth } from "@/contexts/auth-context"
import { useTranslation } from "@/hooks/use-translation"
import { useCodeValues } from "@/hooks/use-code-values"
import type { ClientRequestDetailDto } from "@/types/client-requests"
import type { ClientRequestStatus, ClientRequestType } from "@/types/client-requests"

interface ClientRequestReadonlyDetailProps {
  requestId: string | null
  isOpen: boolean
  onClose: () => void
}

interface FieldRowProps {
  label: string
  value?: string | null
}

function FieldRow({ label, value }: FieldRowProps) {
  if (!value) return null
  return (
    <div className="grid grid-cols-[160px_1fr] gap-3 py-2 border-b border-gray-100 last:border-b-0">
      <span className="text-sm text-gray-500 font-medium">{label}</span>
      <span className="text-sm text-gray-900 break-words">{value}</span>
    </div>
  )
}

interface SectionProps {
  title: string
  children: React.ReactNode
}

function Section({ title, children }: SectionProps) {
  return (
    <div className="mb-6">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-5">{title}</h3>
      <div className="px-5">{children}</div>
    </div>
  )
}

export function ClientRequestReadonlyDetail({ requestId, isOpen, onClose }: ClientRequestReadonlyDetailProps) {
  const panelRef = React.useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [data, setData] = useState<ClientRequestDetailDto | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { t, language } = useTranslation()
  const { token } = useAuth()
  const { getClientRequestDetail } = useClientRequestsApi()
  const codeValues = useCodeValues()

  const resolveRateUnit = (code: string | null | undefined) => {
    if (!code) return undefined
    const text = codeValues.isReady ? codeValues.getCodeValueTextByCode(code, 'workorder.CodeRateUnit') : ''
    return text || code
  }

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        setIsVisible(true)
        panelRef.current?.focus()
      }, 10)
      return () => clearTimeout(timer)
    } else {
      setIsVisible(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (!requestId || !isOpen) return
    let cancelled = false
    setIsLoading(true)
    setError(null)
    setData(null)
    getClientRequestDetail(requestId)
      .then((result) => {
        if (!cancelled) setData(result)
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "An unknown error occurred")
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => { cancelled = true }
  }, [requestId, isOpen, getClientRequestDetail])

  if (!isOpen) return null

  const requestType = data?.requestType as ClientRequestType | undefined
  const status = data?.status as ClientRequestStatus | undefined

  const localizedRequestType =
    requestType && REQUEST_TYPE_TRANSLATION_KEY[requestType]
      ? t(REQUEST_TYPE_TRANSLATION_KEY[requestType])
      : requestType ?? undefined

  const statusStyle = status ? STATUS_BADGE_STYLES[status] : undefined
  const localizedStatus =
    status && STATUS_TRANSLATION_KEY[status]
      ? t(STATUS_TRANSLATION_KEY[status])
      : status ?? undefined

  return (
    <>
      {/* Click-outside backdrop — transparent (no dim, matching the bespoke grid),
          sits just below the panel and closes it on click. */}
      <div
        className="fixed inset-0"
        style={{ zIndex: 1998 }}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="client-request-readonly-detail-title"
      aria-hidden={!isVisible}
      tabIndex={-1}
      className="fixed inset-y-0 right-0 w-[520px] bg-white shadow-xl border-l border-gray-200 flex flex-col font-roboto transition-all duration-300 ease-out outline-none"
      style={{
        zIndex: 1999,
        transform: isVisible ? "translateX(0)" : "translateX(100%)",
        opacity: isVisible ? 1 : 0,
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation()
          onClose()
        }
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex flex-col gap-1">
          {localizedRequestType && (
            <h2 id="client-request-readonly-detail-title" className="text-lg font-semibold text-gray-900">
              {localizedRequestType}
            </h2>
          )}
          {statusStyle && localizedStatus && (
            <StatusBadge style={statusStyle} label={localizedStatus} />
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="h-8 w-8 p-0"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="py-4">
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
              </div>
            )}

            {error && !isLoading && (
              <div className="px-5 py-4 text-sm text-red-600">{error}</div>
            )}

            {data && !isLoading && (
              <>
                {/* Consultant Info */}
                {(data.workerFullName || data.clientOrganization || data.assignedToUserName) && (
                  <Section title={t('consultantInfo')}>
                    <FieldRow label={t('workerFullName')} value={data.workerFullName} />
                    <FieldRow label={t('client')} value={data.clientOrganization} />
                    <FieldRow label={t('assignedTo')} value={data.assignedToUserName} />
                  </Section>
                )}

                {/* Type-specific fields */}
                {requestType === 'Extension' && (
                  <Section title={t('requestDetailsHeader')}>
                    <FieldRow label={t('startDate')} value={formatDateOnly(data.startDate, language)} />
                    <FieldRow label={t('endDate')} value={formatDateOnly(data.endDate, language)} />
                    {data.billRate != null && (
                      <FieldRow
                        label={t('rateLabel')}
                        value={`${data.billRate} ${data.billCurrencyCode ?? ''} / ${resolveRateUnit(data.billRateTypeCode) ?? ''}`.trim()}
                      />
                    )}
                    <FieldRow label={t('requestDetailsField')} value={data.requestDetails} />
                  </Section>
                )}

                {requestType === 'Termination' && (
                  <Section title={t('requestDetailsHeader')}>
                    <FieldRow label={t('endDate')} value={formatDateOnly(data.endDate, language)} />
                    <FieldRow label={t('terminationDetailsField')} value={data.terminationDetails || data.requestDetails} />
                  </Section>
                )}

                {requestType === 'Redeployment' && (
                  <Section title={t('requestDetailsHeader')}>
                    <FieldRow label={t('startDate')} value={formatDateOnly(data.startDate, language)} />
                    <FieldRow label={t('endDate')} value={formatDateOnly(data.endDate, language)} />
                    <FieldRow label={t('jobTitleLabel')} value={data.jobTitle} />
                    <FieldRow label={t('hiringManager')} value={data.hiringManagerName} />
                    <FieldRow label={t('hiringManagerEmail')} value={data.hiringManagerEmail} />
                    {data.billRate != null && (
                      <FieldRow
                        label={t('rateLabel')}
                        value={`${data.billRate} ${data.billCurrencyCode ?? ''} / ${resolveRateUnit(data.billRateTypeCode) ?? ''}`.trim()}
                      />
                    )}
                    <FieldRow label={t('requestDetailsField')} value={data.requestDetails} />
                  </Section>
                )}

                {(requestType === 'General' || requestType === 'Other' || (!requestType && data.requestDetails)) && (
                  <Section title={t('requestDetailsHeader')}>
                    <FieldRow label={t('requestDetailsField')} value={data.requestDetails} />
                  </Section>
                )}

                {/* Attachments */}
                {data.attachments && data.attachments.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-5">
                      {t('attachmentsLabel')}
                    </h3>
                    <div className="px-5 space-y-2">
                      {data.attachments.map((attachment) => (
                        <a
                          key={attachment.fileStorageId}
                          href={getAttachmentDownloadUrl(requestId!, attachment.fileStorageId, token)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline py-1"
                        >
                          <Paperclip className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">{attachment.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </div>
      </div>
    </>
  )
}
